import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPost, operationsClient } from '@/lib/api/client';
import { taxZoneEndpoints } from '@/lib/api/endpoints';
import { taxZoneKeys } from '@/lib/queryKeys';
import type { ApiError } from '@/lib/api/errors';
import type {
  PolygonPreviewRatesEnqueueResponse,
  PolygonPreviewRatesRequest,
  PolygonPreviewRatesResult,
  PolygonPreviewRatesStatusResponse,
} from '@/types/tax-polygon';

/** Poll cadence — fast enough to feel snappy, slow enough not to spam Redis. */
const POLL_INTERVAL_MS = 2_000;

/** Worst-case wall-clock budget for the whole poll. Mirrors the BE job timeout. */
const POLL_TIMEOUT_MS = 2 * 60 * 1_000;

/**
 * Collapsed status surface for the dialog. Maps 1:1 to what the rate
 * panel needs to render:
 *
 * - `idle`: nothing's been enqueued yet (dialog just opened, no ZIPs resolved).
 * - `loading`: job enqueued + polling, no terminal state yet.
 * - `done`: terminal success. `result` is populated.
 * - `failed`: terminal failure (job failed, enqueue 4xx/5xx, or local timeout).
 */
export type PolygonRatesPhase = 'idle' | 'loading' | 'done' | 'failed';

export interface UseTaxZonePolygonPreviewRatesReturn {
  phase: PolygonRatesPhase;
  result: PolygonPreviewRatesResult | undefined;
  errorMessage: string | undefined;
  /** Kick off (or re-kick off) the enqueue+poll cycle for the given ZIPs. */
  start: (zips: string[]) => void;
  /** Reset back to `idle` (e.g. user redraws or closes the dialog). */
  reset: () => void;
}

/**
 * NOT-99 — Phase B of the split polygon preview.
 *
 * Encapsulates: (1) POST to enqueue a rate-fetch job; (2) start polling the
 * status endpoint every 2s; (3) stop polling on terminal state or timeout.
 *
 * Design notes:
 * - Polling is keyed by `jobId` only, so a new enqueue (e.g. user redraws)
 *   invalidates the old query without us having to manually cancel.
 * - We surface a collapsed `phase` rather than exposing the underlying
 *   useMutation + useQuery: the consumer never branches on both at once.
 * - 2-minute hard cap matches the BE job timeout so the FE doesn't poll
 *   a job Redis has already evicted.
 */
export function useTaxZonePolygonPreviewRates(): UseTaxZonePolygonPreviewRatesReturn {
  const [jobId, setJobId] = useState<string | null>(null);
  // Tracks why we ended up in `failed` independently of the underlying
  // query/mutation: a local 2-minute timeout, an enqueue error, or a
  // worker error all produce different messages.
  const [localFailure, setLocalFailure] = useState<string | null>(null);
  // Wall-clock timer that flips us into `failed` if the poll runs too long.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enqueue = useApiMutation<PolygonPreviewRatesEnqueueResponse, PolygonPreviewRatesRequest>({
    mutationFn: (data) =>
      apiPost<PolygonPreviewRatesEnqueueResponse, PolygonPreviewRatesRequest>(
        operationsClient,
        taxZoneEndpoints.previewRates(),
        data,
      ),
    // We render our own error UI on the rate panel — no toast.
    showErrorToast: false,
  });

  const statusQuery = useQuery<PolygonPreviewRatesStatusResponse, ApiError>({
    queryKey: jobId
      ? [...taxZoneKeys.all(), 'preview-rates-status', jobId]
      : [...taxZoneKeys.all(), 'preview-rates-status', 'idle'],
    queryFn: () =>
      apiGet<PolygonPreviewRatesStatusResponse>(
        operationsClient,
        taxZoneEndpoints.previewRatesStatus(jobId as string),
      ),
    enabled: Boolean(jobId),
    // Stop polling once the worker hits a terminal state.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === 'done' || data.status === 'failed')) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
    // Don't refetch a terminal result on focus — it's already final.
    refetchOnWindowFocus: false,
    // Each new jobId is a fresh query — don't reuse a stale cached result.
    staleTime: 0,
    gcTime: 0,
  });

  // Local timeout: if the poll runs longer than POLL_TIMEOUT_MS without
  // hitting a terminal state, treat it as failed. Reset on every new jobId.
  useEffect(() => {
    if (!jobId) return;
    setLocalFailure(null);
    timeoutRef.current = setTimeout(() => {
      setLocalFailure('Tax authority lookup timed out. Please try again.');
    }, POLL_TIMEOUT_MS);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, [jobId]);

  // Clear the wall-clock timer the moment we reach a terminal state — keeps
  // a stray "timed out" failure from clobbering a `done` result.
  useEffect(() => {
    const status = statusQuery.data?.status;
    if (status === 'done' || status === 'failed') {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [statusQuery.data?.status]);

  const start = useCallback(
    (zips: string[]) => {
      setLocalFailure(null);
      setJobId(null);
      if (zips.length === 0) {
        // Empty polygon — surface a synthetic "done" with an empty rollup so
        // the UI doesn't sit forever in a loading state.
        return;
      }
      enqueue.mutate(
        { zips },
        {
          onSuccess: (data) => setJobId(data.jobId),
          onError: () => {
            // The mutation's error state already carries the message — flip
            // a flag so `phase` reads as `failed`.
            setLocalFailure(null);
          },
        },
      );
    },
    [enqueue],
  );

  const reset = useCallback(() => {
    setJobId(null);
    setLocalFailure(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    enqueue.reset();
  }, [enqueue]);

  // ── Collapsed phase ────────────────────────────────────────────────────
  // Order matters: local timeout and enqueue error need to win against a
  // still-pending status query that would otherwise pin us to `loading`.

  let phase: PolygonRatesPhase = 'idle';
  let errorMessage: string | undefined;

  if (localFailure) {
    phase = 'failed';
    errorMessage = localFailure;
  } else if (enqueue.isError) {
    phase = 'failed';
    errorMessage = enqueue.error?.message ?? 'Failed to enqueue tax authority lookup.';
  } else if (statusQuery.data?.status === 'done') {
    phase = 'done';
  } else if (statusQuery.data?.status === 'failed') {
    phase = 'failed';
    errorMessage = statusQuery.data.error ?? 'Tax authority lookup failed on the server.';
  } else if (enqueue.isPending || jobId) {
    phase = 'loading';
  }

  return {
    phase,
    result: statusQuery.data?.result,
    errorMessage,
    start,
    reset,
  };
}
